/**
 * Email Composer Modal
 * 
 * Modal for composing and sending re-engagement emails.
 * Features:
 * - 4 templates: Welcome Back, We Miss You, Getting Started, Custom
 * - Personalization tokens: {first_name}, {book_title}, {site_url}
 * - Preview with sample data
 * - "Send Test to Me" button with [TEST] prefix
 * - Send with progress indicator
 * - Confirmation dialog before sending
 * - Safeguards: 50 user max, shows cooldown skipped count
 */
import React, { useState, useEffect } from "react";
import { 
  X, Send, TestTube, Eye, Mail, Loader2, 
  CheckCircle2, AlertCircle, Lock, AlertTriangle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { API_URL } from '@/config/api';

const EmailComposerModal = ({ 
  open, 
  onOpenChange, 
  selectedUserIds, 
  templates,
  onEmailSent 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [sendStatus, setSendStatus] = useState(null); // { sent: 0, failed: 0, skipped: 0 }

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSelectedTemplate("");
      setSubject("");
      setBody("");
      setShowPreview(false);
      setSendProgress(0);
      setSendStatus(null);
    }
  }, [open]);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject || "");
      setBody(template.body || "");
    }
  };

  const handleSendTest = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }

    setSendingTest(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/marketing/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_ids: selectedUserIds.slice(0, 1), // Use first selected user for sample data
          template_id: selectedTemplate || 'custom',
          subject,
          body,
          is_test: true
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message || "Test email sent to your inbox!");
      } else {
        toast.error(data.message || data.detail || "Failed to send test email");
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      toast.error("Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendClick = () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }

    if (selectedUserIds.length === 0) {
      toast.error("No recipients selected");
      return;
    }

    if (selectedUserIds.length > 50) {
      toast.error("Maximum 50 recipients per send. Please reduce your selection.");
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmedSend = async () => {
    setShowConfirmDialog(false);
    setSending(true);
    setSendProgress(10);
    setSendStatus(null);
    
    try {
      // Simulate progress during API call
      const progressInterval = setInterval(() => {
        setSendProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const response = await fetch(`${API_URL}/api/admin/marketing/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_ids: selectedUserIds,
          template_id: selectedTemplate || 'custom',
          subject,
          body,
          is_test: false
        })
      });
      
      clearInterval(progressInterval);
      setSendProgress(100);
      
      const data = await response.json();
      
      if (response.ok) {
        setSendStatus({
          sent: data.sent_count || 0,
          failed: data.failed_count || 0,
          skipped: data.skipped_cooldown || 0
        });

        if (data.sent_count > 0) {
          toast.success(`Campaign sent! ${data.sent_count} emails delivered.`);
        }
        if (data.skipped_cooldown > 0) {
          toast.warning(`${data.skipped_cooldown} users skipped (in 7-day cooldown)`);
        }
        if (data.failed_count > 0) {
          toast.error(`${data.failed_count} emails failed to send`);
        }
        
        // Close modal after short delay to show results
        setTimeout(() => {
          onEmailSent();
        }, 2000);
      } else {
        toast.error(data.detail || "Failed to send campaign");
        setSendProgress(0);
      }
    } catch (err) {
      console.error('Error sending campaign:', err);
      toast.error("Failed to send campaign");
      setSendProgress(0);
    } finally {
      setSending(false);
    }
  };

  // Preview with sample personalization
  const getPreviewSubject = () => {
    return subject
      .replace(/{first_name}/g, "John")
      .replace(/{book_title}/g, "The Great Adventure")
      .replace(/{site_url}/g, "https://newaudia.com");
  };

  const getPreviewBody = () => {
    return body
      .replace(/{first_name}/g, "John")
      .replace(/{book_title}/g, "The Great Adventure")
      .replace(/{site_url}/g, "https://newaudia.com");
  };

  const recipientCount = selectedUserIds.length;
  const isOverLimit = recipientCount > 50;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Send Re-engagement Email
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Compose and send targeted emails to re-engage inactive users.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* Template Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">Template</Label>
              <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                <SelectTrigger 
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="template-select"
                >
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {templates.map(template => (
                    <SelectItem 
                      key={template.id} 
                      value={template.id}
                      className="text-white hover:bg-slate-700 focus:bg-slate-700"
                    >
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* From Field (locked) */}
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                From
                <Lock className="w-3 h-3 text-slate-500" />
              </Label>
              <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-800/50 border border-slate-700 rounded-md text-slate-400">
                <span>NewAudia &lt;Info@NewAudia.com&gt;</span>
                <Badge variant="outline" className="border-slate-600 text-slate-500 text-xs ml-auto">
                  locked
                </Badge>
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-2">
              <Label className="text-slate-300">To</Label>
              <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-800/50 border border-slate-700 rounded-md">
                <Badge className={isOverLimit ? "bg-red-600" : "bg-blue-600"}>
                  {recipientCount} selected user{recipientCount !== 1 ? 's' : ''}
                </Badge>
                {isOverLimit && (
                  <span className="text-red-400 text-sm flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Max 50 per send
                  </span>
                )}
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-slate-300">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="email-subject-input"
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label className="text-slate-300">Message</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Enter email body..."
                rows={10}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 resize-none font-mono text-sm"
                data-testid="email-body-input"
              />
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="text-slate-500">Personalization tokens:</span>
                <code className="px-1.5 py-0.5 bg-slate-800 rounded text-blue-400">{"{first_name}"}</code>
                <code className="px-1.5 py-0.5 bg-slate-800 rounded text-blue-400">{"{book_title}"}</code>
                <code className="px-1.5 py-0.5 bg-slate-800 rounded text-blue-400">{"{site_url}"}</code>
              </div>
            </div>

            {/* Preview Toggle */}
            {showPreview && (
              <div className="border border-slate-700 rounded-lg p-4 bg-slate-800/50">
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Preview (with sample personalization)
                </h4>
                <div className="space-y-3">
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Subject</span>
                    <p className="text-white mt-1 font-medium">{getPreviewSubject() || '(empty)'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Body</span>
                    <div className="text-slate-300 whitespace-pre-wrap text-sm mt-1 bg-slate-900 p-3 rounded max-h-48 overflow-y-auto">
                      {getPreviewBody() || '(empty)'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Send Progress */}
            {sending && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Sending emails...</span>
                  <span className="text-blue-400">{sendProgress}%</span>
                </div>
                <Progress value={sendProgress} className="h-2" />
              </div>
            )}

            {/* Send Results */}
            {sendStatus && (
              <div className="flex gap-4 p-3 bg-slate-800/50 rounded border border-slate-700">
                <div className="text-center">
                  <span className="text-xl font-bold text-emerald-400">{sendStatus.sent}</span>
                  <p className="text-xs text-slate-400">Sent</p>
                </div>
                <div className="text-center">
                  <span className="text-xl font-bold text-red-400">{sendStatus.failed}</span>
                  <p className="text-xs text-slate-400">Failed</p>
                </div>
                <div className="text-center">
                  <span className="text-xl font-bold text-amber-400">{sendStatus.skipped}</span>
                  <p className="text-xs text-slate-400">Skipped (Cooldown)</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-800">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendTest}
                  disabled={sendingTest || sending || !subject.trim() || !body.trim()}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="send-test-btn"
                >
                  {sendingTest ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4 mr-2" />
                  )}
                  Send Test to Me
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-slate-400 hover:text-white"
                  data-testid="toggle-preview-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? "Hide Preview" : "Preview"}
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={sending}
                  className="text-slate-400 hover:text-white"
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendClick}
                  disabled={sending || !subject.trim() || !body.trim() || recipientCount === 0 || isOverLimit}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="send-campaign-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send to {recipientCount} User{recipientCount !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirm Send</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              You are about to send this email to <strong className="text-white">{recipientCount} user{recipientCount !== 1 ? 's' : ''}</strong>.
              <br /><br />
              Users in the 7-day cooldown period will be automatically skipped.
              <br /><br />
              This action cannot be undone. Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmedSend}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Yes, Send Emails
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EmailComposerModal;
